/**
 * Save-play triggers — the signal layer that turns churn/health movement
 * into a governed save-play proposal.
 *
 * The auditor's finding: playbooks never auto-trigger on health, so risk is
 * only ever discovered by a human reading a dashboard. This module closes
 * the gap deterministically:
 *
 *   evaluateTriggers — reads the explained health assessment
 *     (healthExplain.explainableHealth), the fused churn tier
 *     (healthExplain.churnTier) and the PREVIOUS explained assessment, and
 *     fires typed SaveTriggers. Two honesty rules are structural:
 *       • a 'health_drop' fires ONLY on a band threshold crossing
 *         (green→yellow, yellow→red, green→red) — a recompute that lands in
 *         the same band can never re-fire the alarm, and with no previous
 *         reading a crossing cannot be proven, so nothing fires;
 *       • storm dedupe — a same-account same-kind trigger inside the dedupe
 *         window is dropped, so a flapping signal produces one play, not N.
 *
 *   suggestPlaybook — maps a trigger to a concrete play spec: ordered steps
 *     with relative due dates, the CSM of record as owner, and a
 *     machine-checkable outcomeContract (health_delta over a window, real
 *     ARR at stake — 0 when the account carries no value, never invented).
 *
 * Deliberately PURE: no store, no network, no implicit clock — `now` is
 * always injectable. Works identically with or without the agent worker.
 */

const DAY_MS = 86_400_000;

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export type SaveTriggerKind =
  | 'health_drop'
  | 'renewal_no_exec'
  | 'nps_detractor'
  | 'champion_departed'
  | 'payment_overdue';

/** Minimal account shape the trigger layer reads — structural, never a store read. */
export interface TriggerAccount {
  id: string;
  name: string;
  /** Real ARR in USD when known — never invented. */
  arr?: number;
  /** CSM of record — every spawned step is owned by this human. */
  csmOwner?: string;
}

/** One fired save signal for one account. */
export interface SaveTrigger {
  /** Deterministic: `${kind}:${account.id}` — the dedupe key. */
  id: string;
  kind: SaveTriggerKind;
  account: TriggerAccount;
  /** 0–1 how urgent the save is (churn probability + kind boost). */
  severity: number;
  /** Top causal lines: the crossing/indicator plus the heaviest negative drivers. */
  causes: string[];
  /** Epoch ms the trigger fired (caller-injected clock). */
  firedAt: number;
}

/** Structural subset of healthExplain.ExplainedHealth the triggers read. */
export interface ExplainedLike {
  score: number;
  band: 'red' | 'yellow' | 'green';
  drivers: { signal: string; label: string; deltaPts: number }[];
}

/** Structural subset of healthExplain.ChurnTierResult the triggers read. */
export interface ChurnTierLike {
  tier: 'low' | 'med' | 'high' | 'critical';
  probability: number;
  confidence: number;
  leadingIndicators: string[];
}

/** The per-account input: who it is + its current explained assessment. */
export interface ExplainedAccount {
  account: TriggerAccount;
  health: ExplainedLike;
}

/** Storm-dedupe window: one same-account same-kind trigger per week. */
export const TRIGGER_DEDUPE_WINDOW_MS = 7 * DAY_MS;

export interface EvaluateTriggersOpts {
  /** Clock, epoch ms. Defaults to Date.now() only at the outermost seam. */
  now?: number;
  /** Previously fired triggers (this session / persisted) for storm dedupe. */
  recent?: Pick<SaveTrigger, 'id' | 'firedAt'>[];
  /** Override the dedupe window. */
  dedupeWindowMs?: number;
}

// ---------------------------------------------------------------------------
// evaluateTriggers
// ---------------------------------------------------------------------------

const BAND_RANK: Record<ExplainedLike['band'], number> = { green: 0, yellow: 1, red: 2 };

/** churnTier leading-indicator name → trigger kind. */
const INDICATOR_TO_KIND: Record<string, SaveTriggerKind> = {
  champion_departed: 'champion_departed',
  payment_overdue: 'payment_overdue',
  renewal_soon_no_exec_touch: 'renewal_no_exec',
  nps_detractor: 'nps_detractor',
};

/** Documented per-kind severity boost over the base churn probability. */
export const KIND_SEVERITY_BOOST: Record<SaveTriggerKind, number> = {
  champion_departed: 0.15,
  payment_overdue: 0.1,
  health_drop: 0.1,
  renewal_no_exec: 0.08,
  nps_detractor: 0.08,
};

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** The heaviest negative drivers, formatted for inline display. */
function negativeDriverCauses(health: ExplainedLike, limit: number): string[] {
  return [...health.drivers]
    .filter((d) => d.deltaPts < 0)
    .sort((a, b) => a.deltaPts - b.deltaPts)
    .slice(0, limit)
    .map((d) => `${d.label} −${Math.abs(d.deltaPts).toFixed(1)} pts`);
}

function buildTrigger(
  kind: SaveTriggerKind,
  explained: ExplainedAccount,
  churn: ChurnTierLike,
  leadCause: string,
  now: number,
): SaveTrigger {
  return {
    id: `${kind}:${explained.account.id}`,
    kind,
    account: explained.account,
    severity: round2(clamp01(churn.probability + KIND_SEVERITY_BOOST[kind])),
    // Top-3 causes: the trigger's own cause first, then the heaviest debits.
    causes: [leadCause, ...negativeDriverCauses(explained.health, 2)].slice(0, 3),
    firedAt: now,
  };
}

/**
 * Evaluate one account's explained health + churn tier against its previous
 * assessment and fire typed save triggers.
 *
 *   • 'health_drop' fires ONLY on a band threshold crossing — never on a
 *     same-band recompute, and never without a previous reading to prove
 *     the crossing against.
 *   • Indicator kinds (champion_departed, payment_overdue, renewal_no_exec,
 *     nps_detractor) fire straight off churnTier's NAMED leading indicators.
 *   • Storm dedupe: any trigger whose `${kind}:${accountId}` id appears in
 *     `opts.recent` inside the dedupe window is dropped.
 *
 * Pure and deterministic: same inputs → same triggers.
 */
export function evaluateTriggers(
  explained: ExplainedAccount,
  churnTier: ChurnTierLike,
  prevExplained?: ExplainedLike,
  opts: EvaluateTriggersOpts = {},
): SaveTrigger[] {
  const now = opts.now ?? Date.now();
  const out: SaveTrigger[] = [];

  // Health drop — threshold crossings only, never every recompute.
  if (prevExplained && BAND_RANK[explained.health.band] > BAND_RANK[prevExplained.band]) {
    out.push(
      buildTrigger(
        'health_drop',
        explained,
        churnTier,
        `Health crossed ${prevExplained.band} → ${explained.health.band} (${prevExplained.score} → ${explained.health.score})`,
        now,
      ),
    );
  }

  // Named leading indicators from the churn tier — the causal vocabulary.
  for (const indicator of churnTier.leadingIndicators) {
    const kind = INDICATOR_TO_KIND[indicator];
    if (!kind) continue; // unknown indicator never fires a play on a typo
    if (out.some((t) => t.kind === kind)) continue;
    out.push(buildTrigger(kind, explained, churnTier, `Leading indicator: ${indicator}`, now));
  }

  // Storm dedupe: same account + same kind inside the window fires once.
  const windowMs = opts.dedupeWindowMs ?? TRIGGER_DEDUPE_WINDOW_MS;
  const recent = opts.recent ?? [];
  return out.filter(
    (t) => !recent.some((r) => r.id === t.id && now - r.firedAt < windowMs),
  );
}

// ---------------------------------------------------------------------------
// suggestPlaybook — trigger → concrete play spec
// ---------------------------------------------------------------------------

/** One ordered step of a suggested play; due date = spawn day + offsetDays. */
export interface SavePlayStepSpec {
  /** Stable step key inside the play (dedupe + rollback anchor). */
  key: string;
  title: string;
  offsetDays: number;
  note: string;
  /** Governed action class the step spawns as (reversibility-classified). */
  actionType: string;
}

/** The complete, not-yet-executed play a trigger suggests. */
export interface SavePlaySpec {
  /** Deterministic: `save-play:${kind}:${accountId}`. */
  id: string;
  kind: SaveTriggerKind;
  /** Human name of the play, e.g. 'Re-champion play'. */
  label: string;
  account: TriggerAccount;
  /** CSM of record — the human owner of every step. */
  owner: string;
  severity: number;
  causes: string[];
  steps: SavePlayStepSpec[];
  /** Machine-checkable success criterion the engine seals into a contract. */
  outcomeContract: {
    kind: 'health_delta';
    /** Minimum health-point recovery to count the save as VERIFIED. */
    target: number;
    windowDays: number;
    /** Real ARR at stake — 0 when the account carries no value. */
    dollarsAtStake: number;
  };
}

/** Fallback owner label when the account has no CSM on record. */
export const DEFAULT_CSM_OWNER = 'CSM of record';

interface PlayTemplate {
  label: string;
  target: number;
  windowDays: number;
  steps: SavePlayStepSpec[];
}

/** The per-kind play library — every step is a reversible `create_task`. */
export const PLAY_LIBRARY: Record<SaveTriggerKind, PlayTemplate> = {
  health_drop: {
    label: 'Health recovery play',
    target: 5,
    windowDays: 21,
    steps: [
      {
        key: 'triage-call',
        title: 'Health triage call',
        offsetDays: 1,
        note: 'Call the account within 24h. Walk the health drivers that slipped and confirm the ground truth behind each.',
        actionType: 'create_task',
      },
      {
        key: 'driver-deep-dive',
        title: 'Driver deep-dive',
        offsetDays: 3,
        note: 'Deep-dive the top negative drivers with the account team; agree the two highest-leverage fixes.',
        actionType: 'create_task',
      },
      {
        key: 'remediation-plan',
        title: 'Remediation plan sent',
        offsetDays: 7,
        note: 'Send the written remediation plan with owners and dates; book the follow-up review.',
        actionType: 'create_task',
      },
    ],
  },
  renewal_no_exec: {
    label: 'Renewal exec-engagement play',
    target: 5,
    windowDays: 30,
    steps: [
      {
        key: 'exec-sponsor-intro',
        title: 'Exec sponsor introduction',
        offsetDays: 2,
        note: 'Renewal is inside the window with no exec touch. Line up an exec-to-exec introduction this week.',
        actionType: 'create_task',
      },
      {
        key: 'renewal-review',
        title: 'Renewal business review',
        offsetDays: 7,
        note: 'Run the renewal business review: value delivered, outcomes hit, roadmap ahead.',
        actionType: 'create_task',
      },
      {
        key: 'renewal-packet',
        title: 'Renewal packet sent',
        offsetDays: 14,
        note: 'Send the renewal packet with the value story and proposed terms.',
        actionType: 'create_task',
      },
    ],
  },
  nps_detractor: {
    label: 'Detractor recovery play',
    target: 5,
    windowDays: 14,
    steps: [
      {
        key: 'detractor-response',
        title: 'Respond to detractor feedback',
        offsetDays: 1,
        note: 'Acknowledge the detractor feedback personally within 24h; no templates.',
        actionType: 'create_task',
      },
      {
        key: 'root-cause-followup',
        title: 'Root-cause follow-up',
        offsetDays: 5,
        note: 'Close the loop: what caused the score, what we changed, what they should see next.',
        actionType: 'create_task',
      },
    ],
  },
  champion_departed: {
    label: 'Re-champion play',
    target: 5,
    windowDays: 30,
    steps: [
      {
        key: 'map-power-structure',
        title: 'Map the power structure',
        offsetDays: 2,
        note: 'Our champion departed. Map who now owns the budget, the outcome, and the day-to-day.',
        actionType: 'create_task',
      },
      {
        key: 'recruit-new-champion',
        title: 'Identify and recruit a new champion',
        offsetDays: 5,
        note: 'Shortlist candidate champions from the mapped structure; open a 1:1 with the strongest.',
        actionType: 'create_task',
      },
      {
        key: 'exec-intro',
        title: 'Exec-to-exec introduction',
        offsetDays: 7,
        note: 'Bridge the relationship gap at the top while the new champion ramps.',
        actionType: 'create_task',
      },
      {
        key: 'champion-enablement',
        title: 'Champion enablement session',
        offsetDays: 14,
        note: 'Arm the new champion: value story, internal wins, and the artifacts to sell us internally.',
        actionType: 'create_task',
      },
    ],
  },
  payment_overdue: {
    label: 'Billing rescue play',
    target: 3,
    windowDays: 14,
    steps: [
      {
        key: 'verify-invoice',
        title: 'Verify invoice and contact AP',
        offsetDays: 1,
        note: 'Confirm the overdue invoice is correct and reach the right AP contact — most overdue payments are process, not intent.',
        actionType: 'create_task',
      },
      {
        key: 'payment-plan',
        title: 'Offer a payment path',
        offsetDays: 5,
        note: 'If the miss is real, agree a payment plan before it curdles into churn intent.',
        actionType: 'create_task',
      },
    ],
  },
};

/**
 * Map a fired trigger to its concrete play spec: the kind's step template,
 * the CSM of record as owner, relative due dates, and a machine-checkable
 * health_delta outcome contract staking the account's REAL ARR (0 when the
 * account carries none — never invented). Pure and deterministic.
 */
export function suggestPlaybook(trigger: SaveTrigger): SavePlaySpec {
  const template = PLAY_LIBRARY[trigger.kind];
  return {
    id: `save-play:${trigger.kind}:${trigger.account.id}`,
    kind: trigger.kind,
    label: template.label,
    account: trigger.account,
    owner: trigger.account.csmOwner ?? DEFAULT_CSM_OWNER,
    severity: trigger.severity,
    causes: [...trigger.causes],
    steps: template.steps.map((s) => ({ ...s })),
    outcomeContract: {
      kind: 'health_delta',
      target: template.target,
      windowDays: template.windowDays,
      dollarsAtStake: trigger.account.arr ?? 0,
    },
  };
}
